import React, { useEffect, useMemo, useCallback } from "react";
import Moment from "react-moment";
import UploadDocPopup from "../UploadDocPopup";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoadCandidateAppsQuery,
  useLoadAppRuningTasksQuery,
  useLoadAppDocumentsQuery,
  useLoadInvoiceDocumentsQuery,
  useLoadReqDocumentsQuery,
} from "../../app/store/candidateApiSlice";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../../app/store/auth";
import { getAlert } from "../../Services/appAlerts";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";
import { getComponentUserStatus, UpdateComponentsUserStatus } from "../../app/store/tools";
import { useState } from "react";

const SENT_DOCS_STYLES = {
  pageWrap: {
    padding: "8px 0 4px 0"
  },
  shell: {
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  hero: {
    padding: "14px 20px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    border: "1px solid #e5e5e5",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  titleCopy: {
    minWidth: 0
  },
  title: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#000000",
    margin: 0
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  uploadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  body: {
    padding: "0 22px 22px 22px",
    display: "grid",
    gap: "20px"
  },
  sectionCard: {
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    overflow: "hidden"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  sectionTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0
  },
  sectionIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid #e2e8f0"
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#0f172a"
  },
  sectionMeta: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0,
    fontWeight: "normal"
  },
  sectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 10px",
    borderRadius: "0px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  table: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e5e5",
    padding: "14px 16px"
  },
  cell: {
    padding: "14px 16px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    verticalAlign: "top",
    fontWeight: 600
  },
  dateText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  fileButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#2563eb",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "left"
  },
  fileSubtext: {
    fontSize: "11.5px",
    color: "#64748b",
    marginTop: "4px",
    fontWeight: 600
  },
  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "0px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  notesText: {
    whiteSpace: "pre-wrap",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#475569",
    fontWeight: 600
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 14px auto",
    borderRadius: "0px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "26px"
  },
  emptyTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center"
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: "10px auto 0 auto",
    fontWeight: "normal"
  }
};

const ROUTE_CONFIG_PARTNER = [{ name: "Contract Management", url: "" }];

export default function SentDocs() {
  const [uploadDocShowHide, setUploadDocShowHide] = useState(false);

  const [wfTask, setWfTask] = useState(null);

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.linkUser || currentUser?.id;
  const currentUserIdRaw = currentUser?.id;

  const partnerId = (userDetailsSelector?.approved > 0) ? userDetailsSelector?.partnerid : null;

  // RTK Query: Declarative Loading of Candidate Apps
  const { data: candidateApps = [] } = useLoadCandidateAppsQuery(currentUserId, { skip: !currentUserId });

  const workflow = useMemo(() => {
    if (candidateApps.length > 0) {
      const mainWorkflow = candidateApps.filter(x => x.workflowType === 1);
      return mainWorkflow.length > 0 ? mainWorkflow[0] : null;
    }
    return null;
  }, [candidateApps]);

  const { data: appTasksSelector = [] } = useLoadAppRuningTasksQuery(
    { wfId: workflow?.oldWorkflowId, userId: currentUserId },
    { skip: !workflow || !currentUserId }
  );

  const appTasklist = useMemo(() => {
    return appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
  }, [appTasksSelector]);

  const { data: appDocuments = [], refetch: refetchAppDocuments } = useLoadAppDocumentsQuery(
    { wfId: workflow?.oldWorkflowId, userId: currentUserId },
    { skip: !workflow || !currentUserId }
  );

  const { data: invoiceDocuments = [] } = useLoadInvoiceDocumentsQuery(partnerId, { skip: !partnerId });
  const { data: reqDocuments = [] } = useLoadReqDocumentsQuery(partnerId, { skip: !partnerId });

  useEffect(() => {
    let targetUsrId = currentUser?.linkUser || currentUser?.id;
    if (!userDetailsSelector && targetUsrId) {
      dispatch(loadUserDetails(targetUsrId));
    }
  }, [dispatch, currentUser, userDetailsSelector]);

  const sentDocs = useMemo(() => {
    if (!appDocuments) return [];
    return appDocuments.filter(x => x.docCategory === "sent");
  }, [appDocuments]);

  const receivedDocs = useMemo(() => {
    let recList = [];
    if (appDocuments.length > 0) {
      const docs = appDocuments.filter(x => x.docCategory === "recieved" || x.docCategory === "received");
      recList = [...recList, ...docs];
    }
    if (invoiceDocuments.length > 0) {
      recList = [...recList, ...invoiceDocuments];
    }
    if (reqDocuments.length > 0) {
      recList = [...recList, ...reqDocuments];
    }
    return recList;
  }, [appDocuments, invoiceDocuments, reqDocuments]);

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 20);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Sent Document",
      performerId: currentUserIdRaw,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 20
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }, [componentUserStatusSelector, currentUserIdRaw, dispatch]);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Document History"));
    dispatch(setCurrentRoute(ROUTE_CONFIG_PARTNER));
  }, [dispatch]);

  useEffect(() => {
    if (sentDocs.length > 0) {
      let docItems = "";
      sentDocs.forEach((x, key) => {
        if (key > 0) docItems += ",";
        docItems += x.id;
      });

      handleUpdateComponent(docItems, sentDocs[0].ciid);
    }
  }, [sentDocs, handleUpdateComponent]);

  const { downloadFile } = useDocDownload();

  const handleDownloadFile = (doc) => {
    downloadFile(doc);
  };

  const handleUploadDocModalShowHide = useCallback((app) => {
    if (app === "save") {
      setUploadDocShowHide(false);
      refetchAppDocuments();
    }
    else {
      const reviewApp = appTasklist.filter((x) => x.registrationType === "WP");
      if (reviewApp.length > 0) {
        const lastReviewer = reviewApp[0];
        setWfTask([lastReviewer]);
      } else {
        getAlert("", "No reviewer found", "error");
      }
      setUploadDocShowHide(prev => !prev);
    }
  }, [appTasklist, refetchAppDocuments]);

  const getDocType = useCallback((type) => {
    if (type === "sharing")
      return "Contract";
    else if (type?.substring(0, 3) === "ach")
      return "ACH Void Check";
    else if (type?.substring(0, 9) === "insurance")
      return "COI Insurance";
    else if (type?.substring(0, 3) === "req" || type?.includes("_c2c_"))
      return "Required";
    else return type;
  }, []);

  const totalDocuments = sentDocs.length + receivedDocs.length;
  const totalSentDocuments = sentDocs.length;
  const totalReceivedDocuments = receivedDocs.length;

  return (
    <React.Fragment>
      <div className="col-md-12" style={SENT_DOCS_STYLES.pageWrap}>
        <div style={SENT_DOCS_STYLES.shell}>
          <div style={SENT_DOCS_STYLES.hero}>
            <div style={SENT_DOCS_STYLES.heroRow}>
              <div style={SENT_DOCS_STYLES.titleWrap}>
                <div style={SENT_DOCS_STYLES.titleIcon}>
                  <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                </div>
                <div style={SENT_DOCS_STYLES.titleCopy}>
                  <h1 style={SENT_DOCS_STYLES.title}>Document History</h1>
                  <p style={SENT_DOCS_STYLES.subtitle}>Review all sent and received documents across your workflow.</p>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={SENT_DOCS_STYLES.refreshButton}
                  onClick={() => refetchAppDocuments()}
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={SENT_DOCS_STYLES.uploadButton}
                  onClick={() => handleUploadDocModalShowHide("")}
                >
                  <i className="fa fa-upload" aria-hidden="true"></i>
                  Upload Document
                </button>
              </div>
            </div>
          </div>
          <div style={SENT_DOCS_STYLES.statsRow}>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-files-o" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Total Documents</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalDocuments}</div>
              </div>
            </div>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-upload" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Sent</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalSentDocuments}</div>
              </div>
            </div>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-download" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Received</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalReceivedDocuments}</div>
              </div>
            </div>
          </div>
          <div style={SENT_DOCS_STYLES.body}>
            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-upload" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Sent Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files sent out from your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{sentDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>ID</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Sent</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentDocs.length > 0 ? sentDocs.map((item, key) => (
                      <tr key={item.id || `sent-${key}`}>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={{ color: "#0f172a", fontWeight: 700 }}>#</span>{" "}
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>{item.id || key + 1}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                              <Moment format="MMM Do, YYYY">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                              <Moment format="h:mm a">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                          </div>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <button
                            className="btn btn-sm"
                            style={SENT_DOCS_STYLES.fileButton}
                            title="Download Document"
                            onClick={() => handleDownloadFile(item)}
                          >
                            <i className="fa fa-file-text-o" aria-hidden="true"></i>
                            {item.documentName}
                          </button>
                          {item.docModifiedName && (
                            <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                          )}
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.uploadType)}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-upload" aria-hidden="true"></i>
                            </div>
                            <h4 style={SENT_DOCS_STYLES.emptyTitle}>No sent documents yet</h4>
                            <p style={SENT_DOCS_STYLES.emptyText}>
                              Documents you send through the workflow will appear here for quick download and review.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-download" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Received Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files returned or shared back into your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{receivedDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>ID</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Received</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivedDocs.length > 0 ? receivedDocs.map((item, key) => (
                      <tr key={item.id || `received-${key}`}>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={{ color: "#0f172a", fontWeight: 700 }}>#</span>{" "}
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>{item.id || key + 1}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                              <Moment format="MMM Do, YYYY">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                              <Moment format="h:mm a">
                                {item.createdDateTime}
                              </Moment>
                            </span>
                          </div>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <button
                            className="btn btn-sm"
                            style={SENT_DOCS_STYLES.fileButton}
                            title="Download Document"
                            onClick={() => handleDownloadFile(item)}
                          >
                            <i className="fa fa-file-text-o" aria-hidden="true"></i>
                            {item.documentName}
                          </button>
                          {item.docModifiedName && (
                            <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                          )}
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.docType)}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-download" aria-hidden="true"></i>
                            </div>
                            <h4 style={SENT_DOCS_STYLES.emptyTitle}>No received documents yet</h4>
                            <p style={SENT_DOCS_STYLES.emptyText}>
                              Received documents will show up here once files are shared back into your workflow.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadDocShowHide &&

        <UploadDocPopup
          uploadDocShowHide={uploadDocShowHide}
          handleUploadDocModalShowHide={handleUploadDocModalShowHide}
          runningApplication={wfTask}
        ></UploadDocPopup>}
    </React.Fragment>
  );
}
